import { STABILITY_MODEL } from '../sim/stability.ts';
import { GRAIN_LABELS } from '../sim/snowpack.ts';
import { AVALANCHE_PROBLEM_LABELS } from '../sim/stability.ts';
import { type HoverProbe, uiMode } from '../state/store.ts';
import {
  formatAspect,
  formatCompass16,
  formatNumber,
  formatPhase,
  formatSigned,
  formatTemperature,
  skierClass,
} from './format.ts';

export interface HoverReadoutProps {
  probe: HoverProbe | null;
}

/** Simple mode's reduced hover readout: just the essentials a beginner needs, no storm/wind
 * jargon and no raw stability index (a poor/fair/good class instead). */
function SimpleHoverReadout({ probe }: { probe: HoverProbe }) {
  return (
    <div class="hover-readout">
      <dl>
        <dt>Elevation</dt>
        <dd>{formatNumber(probe.elevation)} m</dd>
        <dt>Slope</dt>
        <dd>{formatNumber(probe.slope, 1)}°</dd>
        <dt>Aspect</dt>
        <dd>{formatAspect(probe.aspect)}</dd>
        <dt>Snow depth</dt>
        <dd>{formatNumber(probe.snowDepth * 100)} cm</dd>
        {probe.skier !== undefined ? (
          <>
            <dt>Stability</dt>
            {/* Below ~30° snow rarely slides even when a weak layer can crack (a "whumpf"), so
                a skier index of "poor" would read as "dangerous here" in simple mode. */}
            <dd>
              {probe.slope < STABILITY_MODEL.problemMinSlope
                ? 'too gentle to slide'
                : skierClass(probe.skier)}
            </dd>
          </>
        ) : null}
        {probe.avDebrisDepth !== undefined ? (
          <>
            <dt>Debris depth</dt>
            <dd>{formatNumber(probe.avDebrisDepth * 100)} cm</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}

/** Small readout of the terrain point under the cursor, bottom-right of the 3D view. Storm-derived
 * rows (snowfall, wind, Sx, rain) only appear once a storm result exists for this point. */
export function HoverReadout({ probe }: HoverReadoutProps) {
  if (!probe) return null;
  if (uiMode.value === 'simple') return <SimpleHoverReadout probe={probe} />;
  const hasStorm = probe.stormSnowfall !== undefined;
  return (
    <div class="hover-readout">
      <dl>
        <dt>Elevation</dt>
        <dd>{formatNumber(probe.elevation)} m</dd>
        <dt>Slope</dt>
        <dd>{formatNumber(probe.slope, 1)}°</dd>
        <dt>Aspect</dt>
        <dd>{formatAspect(probe.aspect)}</dd>
        <dt>Air temp.</dt>
        <dd>{formatTemperature(probe.airTemp, 1)} °C</dd>
        <dt>Snow depth</dt>
        <dd>{formatNumber(probe.snowDepth * 100)} cm</dd>
        <dt>Precipitation</dt>
        <dd>{formatPhase(probe.snowFraction)}</dd>
        {probe.layerCount !== undefined ? (
          <>
            <dt>Layers</dt>
            <dd>{probe.layerCount}</dd>
          </>
        ) : null}
        {probe.topGrain !== undefined ? (
          <>
            <dt>Top layer</dt>
            <dd>{GRAIN_LABELS[probe.topGrain]}</dd>
          </>
        ) : null}
        {probe.skier !== undefined ? (
          <>
            <dt>Skier stability</dt>
            <dd>
              {formatNumber(probe.skier, 2)} ({skierClass(probe.skier)})
            </dd>
          </>
        ) : null}
        {probe.problem !== undefined ? (
          <>
            <dt>Avalanche problem</dt>
            <dd>{AVALANCHE_PROBLEM_LABELS[probe.problem]}</dd>
          </>
        ) : null}
        {probe.criticalGrain !== undefined ? (
          <>
            <dt>Critical layer</dt>
            <dd>
              {GRAIN_LABELS[probe.criticalGrain]} at{' '}
              {formatNumber((probe.criticalDepth ?? 0) * 100)} cm
            </dd>
          </>
        ) : null}
        {hasStorm ? (
          <>
            <dt>Storm snowfall</dt>
            <dd>{formatNumber(probe.stormSnowfall! * 100)} cm</dd>
            <dt>Wind change</dt>
            <dd>{formatSigned((probe.windChange ?? 0) * 100)} cm</dd>
            {probe.windSpeed !== undefined ? (
              <>
                <dt>Wind speed</dt>
                <dd>{formatNumber(probe.windSpeed, 1)} m/s</dd>
              </>
            ) : null}
            {probe.windFromTrueDeg !== undefined ? (
              <>
                <dt>Wind from</dt>
                <dd>{formatCompass16(probe.windFromTrueDeg)}</dd>
              </>
            ) : null}
            {probe.sx !== undefined ? (
              <>
                <dt>Shelter (Sx)</dt>
                <dd>{formatNumber(probe.sx, 1)}°</dd>
              </>
            ) : null}
            <dt>Rain</dt>
            <dd>{formatNumber(probe.rain ?? 0, 1)} mm</dd>
          </>
        ) : null}
        {probe.avDebrisDepth !== undefined ? (
          <>
            <dt>Debris depth</dt>
            <dd>{formatNumber(probe.avDebrisDepth * 100)} cm</dd>
            <dt>Removed</dt>
            <dd>{formatNumber((probe.avRemovedDepth ?? 0) * 100)} cm</dd>
            <dt>Net change</dt>
            <dd>{formatSigned((probe.avNetChange ?? 0) * 100)} cm</dd>
          </>
        ) : null}
        {probe.avOriginSharePct !== undefined ? (
          <>
            <dt>From entrainment</dt>
            <dd>{formatNumber(probe.avOriginSharePct, 0)}%</dd>
          </>
        ) : null}
        {probe.avMaxSpeed !== undefined ? (
          <>
            <dt>Max speed here</dt>
            <dd>{formatNumber(probe.avMaxSpeed, 1)} m/s</dd>
          </>
        ) : null}
        {probe.avMaxPressureKPa !== undefined ? (
          <>
            <dt>Max pressure here</dt>
            <dd>{formatNumber(probe.avMaxPressureKPa, 1)} kPa</dd>
          </>
        ) : null}
      </dl>
    </div>
  );
}
